import { useIntl } from '../../hooks';
import { formatOptionalDateTime } from '../../lib/date-time.js';
import { hzToMhz } from '../../lib/frequency.js';
import { formatOptionalNumber } from '../../lib/number-format.js';
import type { MultiFrequencySuppression } from '../../types';
import { Badge, TableCell, TableRow } from '../ui';

type MultiFrequencyDetailsRowProps = {
  /**
   * `null` when the group is genuinely synthetic but its immutable snapshot could not be read —
   * an unknown schema version or a malformed blob. The row still renders, because the alternative
   * (falling back to the detections row) would fire a request for detections a synthetic group
   * cannot have.
   */
  suppression: MultiFrequencySuppression | null;
  colSpan: number;
};

/**
 * Rendered INSTEAD of `DetectionGroupDetectionsRow`: a synthetic group has no detections, so that
 * component would issue a request guaranteed to return nothing. Every value comes from the payload
 * already in memory — no fetch, no join.
 */
export function MultiFrequencyDetailsRow({ suppression, colSpan }: MultiFrequencyDetailsRowProps) {
  const intl = useIntl();

  if (!suppression) {
    return (
      <TableRow data-testid="multi-frequency-details-row">
        <TableCell colSpan={colSpan} className="bg-background-light p-4">
          <span
            className="text-xs text-muted-foreground"
            data-testid="multi-frequency-details-unavailable"
          >
            {intl.formatMessage({ id: 'detections.multiFrequency.unavailable' })}
          </span>
        </TableCell>
      </TableRow>
    );
  }

  // Sorted here too so the display guarantee does not depend on the producer.
  const frequenciesMhz = [...suppression.channelFrequencyList]
    .sort((a, b) => a - b)
    .map((hz) => hzToMhz(hz));

  const facts: { label: string; value: string }[] = [
    {
      label: intl.formatMessage({ id: 'detections.multiFrequency.signalType' }),
      value: intl.formatMessage({ id: `signalType.${suppression.type}` }),
    },
    {
      label: intl.formatMessage({ id: 'detections.multiFrequency.bandwidth' }),
      value: intl.formatMessage(
        { id: 'detections.multiFrequency.bandwidthValue' },
        { value: formatOptionalNumber(hzToMhz(suppression.bandwidth)) },
      ),
    },
    {
      label: intl.formatMessage({ id: 'detections.multiFrequency.stepStandingTime' }),
      value: intl.formatMessage(
        { id: 'detections.multiFrequency.stepStandingTimeValue' },
        { value: suppression.stepStandingTimeMs },
      ),
    },
    {
      label: intl.formatMessage({ id: 'detections.multiFrequency.duration' }),
      value: intl.formatMessage(
        { id: 'detections.multiFrequency.durationValue' },
        { value: suppression.duration },
      ),
    },
    {
      label: intl.formatMessage({ id: 'detections.multiFrequency.startedAt' }),
      value: formatOptionalDateTime(suppression.startedAt, intl.locale),
    },
    {
      label: intl.formatMessage({ id: 'detections.multiFrequency.finishedAt' }),
      value: formatOptionalDateTime(suppression.finishedAt, intl.locale),
    },
  ];

  return (
    <TableRow data-testid="multi-frequency-details-row">
      <TableCell colSpan={colSpan} className="bg-background-light p-4">
        <div className="flex flex-col gap-3 min-w-0">
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-xs font-medium text-foreground">
              {intl.formatMessage(
                { id: 'detections.multiFrequency.frequencies' },
                { count: frequenciesMhz.length },
              )}
            </span>
            <div className="flex flex-wrap gap-1" data-testid="multi-frequency-details-list">
              {frequenciesMhz.map((mhz) => (
                <Badge
                  key={mhz}
                  variant="secondary"
                  className="font-mono text-xs"
                  // The numeric value, unformatted and unlocalised, so display assertions do not
                  // depend on locale formatting or on how a test mocks the translator.
                  data-frequency-mhz={mhz}
                >
                  {intl.formatMessage(
                    { id: 'broadbandSuppressionsDialog.form.multi.frequencyBadge' },
                    { value: formatOptionalNumber(mhz) },
                  )}
                </Badge>
              ))}
            </div>
          </div>

          <dl className="flex flex-wrap gap-x-6 gap-y-2">
            {facts.map((fact) => (
              <div key={fact.label} className="flex flex-col">
                <dt className="text-xs text-muted-foreground">{fact.label}</dt>
                <dd className="text-sm font-mono text-foreground">{fact.value}</dd>
              </div>
            ))}
          </dl>
        </div>
      </TableCell>
    </TableRow>
  );
}
